import { setCookie, deleteCookie } from '@atlassian/browser-storage-controls/cookie-controls';

import { getCookie } from '@atlassian/jira-browser-storage-controls/src/utils/get-cookie/index.tsx';
import type { CookieCircuitBreakerConfig as JiraSpaRouteCookieCircuitBreakerConfig } from '@atlassian/jira-route-types/src/JiraSpaRoute.tsx';

type CircuitBreakerState = 'CLOSED' | 'OPEN' | 'HALF_OPEN';

type Result = 'PASS' | 'FAIL';

interface CookieData {
	numberRepresentation: number; // sliding window of 0s and 1s
	windowLength: number;
	counter: number;
	version: number;
}
// for details on these properties, see circuitBreakerConfig in jira/src/packages/platform/routing/route-types/src/JiraSpaRoute.tsx
export type CookieCircuitBreakerConfig = Pick<
	JiraSpaRouteCookieCircuitBreakerConfig,
	'cookieKey' | 'windowSize' | 'threshold' | 'keepOpenCount' | 'halfOpenForCount'
> & { version?: number };
export class CookieCircuitBreaker {
	// Values from CookieCircuitBreakerConfig
	private threshold: number;

	private keepOpenCount: number;

	private halfOpenForCount: number;

	private cookieKey: string;

	private windowSize: number;

	private version: number;

	// mask generated based on the windowSize, used to truncate the oldest value in the sliding window once it reaches windowSize.
	// i.e for window size of 4, windowMask will be 0b1111
	private windowMask: number;

	// deserialized data from the cookie (or that will be saved to the cookie, after serialization)
	private cookieData: CookieData;

	constructor({
		cookieKey,
		windowSize = 15,
		threshold = 0.1,
		keepOpenCount = 100,
		halfOpenForCount = 10,
		// version is only required when setting the cookie (i.e client side)
		version = 0,
	}: CookieCircuitBreakerConfig) {
		this.cookieKey = cookieKey;
		this.windowSize = windowSize > 29 ? 29 : windowSize;
		// eslint-disable-next-line no-bitwise
		this.windowMask = (1 << this.windowSize) - 1;
		this.threshold = threshold;
		this.halfOpenForCount = halfOpenForCount;
		this.keepOpenCount = keepOpenCount;
		this.version = version;

		const existingCookie = getCookie(`cb_${cookieKey}`, true);
		if (existingCookie) {
			this.cookieData = this.parseCookieData(existingCookie);
		} else {
			this.cookieData = {
				numberRepresentation: 0,
				counter: 0,
				windowLength: 0,
				version,
			};
		}
	}

	private parseCookieData(cookieValue: string): CookieData {
		try {
			const [encodedWindow, stringWindowLength, stringCount, stringVersion] =
				cookieValue.split('|');

			const parsedNumberRepresentation = Number.parseInt(encodedWindow ?? '', 36);
			const numberRepresentation = !Number.isNaN(parsedNumberRepresentation)
				? parsedNumberRepresentation
				: 0;

			const parsedCounter = Number.parseInt(stringCount ?? '', 36);
			const counter = !Number.isNaN(parsedCounter) ? parsedCounter : 0;

			const parsedWindowLength = Number.parseInt(stringWindowLength ?? '', 36);
			const windowLength = !Number.isNaN(parsedWindowLength) ? parsedWindowLength : 0;

			const parsedVersion = Number.parseInt(stringVersion ?? '', 36);
			const version = !Number.isNaN(parsedVersion) ? parsedVersion : 0;
			return {
				numberRepresentation,
				counter,
				windowLength,
				version,
			};
		} catch (error) {
			// Return default data if parsing fails
			return {
				numberRepresentation: 0,
				counter: 0,
				windowLength: 0,
				version: this.version,
			};
		}
	}

	private serializeForCookie(): string {
		const encodedWindow = this.cookieData.numberRepresentation.toString(36);
		const stringCount = this.cookieData.counter.toString(36);
		const windowLength = this.cookieData.windowLength.toString(36);
		const version = this.cookieData.version.toString(36);
		return `${encodedWindow}|${windowLength}|${stringCount}|${version}`;
	}

	public saveToCookie(): void {
		setCookie(`cb_${this.cookieKey}`, this.serializeForCookie(), {
			expires: 14,
			secure: true,
			sameSite: 'strict',
		});
	}

	public resetCookieIfVersionMismatch(): boolean {
		if (this.cookieData.version !== this.version) {
			deleteCookie(`cb_${this.cookieKey}`);
			this.cookieData = {
				numberRepresentation: 0,
				counter: 0,
				windowLength: 0,
				version: this.version,
			};
			return true;
		}
		return false;
	}

	private addResultToWindow(result: Result): void {
		// eslint-disable-next-line no-bitwise
		this.cookieData.numberRepresentation <<= 1; // Shift left by 1
		/**
		 * For PASS, we bitwise OR with 1 to "add" 1 to the least-significant bit
		 * For FAIL, we bitwise OR with 0 which will be no-op - the LSB will already be 0 after the shift left above
		 */
		// eslint-disable-next-line no-bitwise
		this.cookieData.numberRepresentation |= result === 'PASS' ? 1 : 0;

		/**
		 * Truncate to the window size using bitwise AND
		 *
		 * This is the equivalent operation to this (if it was an array):
		 *
		 * ```
		 * if (numberRepresentationAsArray.length > this.windowSize) {
		 * 	   // in this analogy, the end of the array is the most significant bit.
		 *     numberRepresentationAsArray.pop();
		 * }
		 * ```
		 */
		// eslint-disable-next-line no-bitwise
		this.cookieData.numberRepresentation &= this.windowMask;

		/**
		 * Only increase the windowLength if it is under the windowSize
		 *
		 * We don't increase when it is equal because the truncation we just did above
		 * will keep it at windowLength = windowSize.
		 *
		 * We need to keep track of windowLength separately because we cannot derive it
		 * from the numberRepresentation when the oldest value in the window is 0.
		 * i.e the numeric representation of 00000001 is the same as 1 - but they have a different
		 * meaning here.
		 */
		if (this.cookieData.windowLength < this.windowSize) {
			this.cookieData.windowLength++;
		} else {
			this.cookieData.windowLength = this.windowSize;
		}
	}

	private incrementOrResetOpenCounter() {
		if (this.cookieData.counter >= this.keepOpenCount + this.halfOpenForCount) {
			this.cookieData.counter = 0;
		} else {
			this.cookieData.counter++;
		}
	}

	public recordFailure() {
		return this.recordResult('FAIL');
	}

	public recordPass() {
		return this.recordResult('PASS');
	}

	public recordResult(result: Result): {
		state: CircuitBreakerState;
		pass: number;
		fail: number;
		ratio: number;
	} {
		const state = this.getState();
		switch (state.state) {
			case 'CLOSED':
				this.addResultToWindow(result);
				// reset the counter when circuit is closed
				this.cookieData.counter = 0;
				break;
			case 'OPEN':
				this.cookieData.counter++;
				break;
			case 'HALF_OPEN':
				this.incrementOrResetOpenCounter();
				this.addResultToWindow(result);
				break;
			default:
				throw new Error(`Unknown state: ${state}`);
		}
		this.saveToCookie();
		return state;
	}

	public getWindowSize(): number {
		return this.windowSize;
	}

	public getKey(): string {
		return this.cookieKey;
	}

	public isAtWindowSize(): boolean {
		return this.cookieData.windowLength === this.windowSize;
	}

	public getState(): {
		state: CircuitBreakerState;
		pass: number;
		fail: number;
		ratio: number;
		count: number;
		version: number;
	} {
		/**
		 * To count the number of passes, we convert the number to a base 2 string and count the number of 1s.
		 *
		 * This is a simple way of doing it, and should be pretty quick because it uses a regexp literal
		 * it is only called once per page load regardless.
		 */
		const onesAndZeroes = this.cookieData.numberRepresentation.toString(2);
		const pass = onesAndZeroes.match(/1/g)?.length ?? 0;
		const fail = this.cookieData.windowLength - pass;
		const ratio = this.cookieData.windowLength > 0 ? pass / this.cookieData.windowLength : 1;
		let state: CircuitBreakerState = 'CLOSED';
		if (this.isAtWindowSize() && ratio < 1 - this.threshold) {
			if (
				this.cookieData.counter > this.keepOpenCount &&
				this.cookieData.counter <= this.keepOpenCount + this.halfOpenForCount
			) {
				state = 'HALF_OPEN';
			} else {
				state = 'OPEN';
			}
		}
		return {
			state,
			pass,
			fail,
			ratio,
			count: this.cookieData.counter,
			version: this.cookieData.version,
		};
	}
}
